import { useTranslation } from '@klv/hooks/useTranslation';
import { generateClassName } from '@klv/shared/utils/functions';

import CountdownBadge from './atoms/CountdownBadge';
import { CertificateBadge } from '@klv/molecules/unified-search-result-grid-item/atoms/CertificateBadge';
import CourseList from './course';
import Duration from './atoms/Duration';
import Icon from '@klv/atoms/icon';
import NextEventDetails from './atoms/NextEventDetails';

import * as s from './styles/unified-search-result-grid-item.module.css';

import type { ICourseStatus, IUnifiedSearchResult } from '@klv/molecules/unified-search-results/types';
import type { IGridItemStyle } from './types';

const UnifiedSearchResultGridItem = ({
  courseId,
  description = '',
  duration,
  divider = true,
  link,
  metadata = '',
  nextEvents = [],
  parentBackgroundColor = 'light',
  pill,
  sendEvent,
  title,
  truncateText = true,
  userCourseData,
  viewedResources,
  ...props
}: IUnifiedSearchResult) => {
  const { t } = useTranslation();
  const hasPill = pill && pill.length > 0;
  const isLiveTraining = pill && (/^Live training$/i)?.test(pill);
  const showNextEventDetails = isLiveTraining && nextEvents.some((event) => event.start_time);

  const resourceStripeMap = (tags: string): IGridItemStyle => {
    if (tags.includes('Best practice'))
      return { style: s.bestPractice, text: 'Best practice' };
    else if (tags.includes('Live training'))
      return { style: s.liveTraining, text: 'Live training' };
    else if (tags.includes('Quick guide'))
      return { style: s.quickGuide, text: 'Quick guide' };
    else if (tags.includes('Getting started'))
      return { style: s.gettingStarted, text: 'Getting Started' };
    else if (tags.includes('Certificate'))
      return { style: s.certificate, text: 'Certificate' };
    else if (tags.includes('Course'))
      return { style: s.course, text: 'Course' };

    return { style: '', text: tags };
  };

  let cardStyle = { style: '', text: pill || '' };
  if (pill) {
    cardStyle = resourceStripeMap(pill);
  }

  const renderCourseContent = () => {
    const matchingCourse: any = userCourseData?.course_progress.find(
      (course: ICourseStatus) => course.third_party_id === courseId,
    );
    if (matchingCourse) {
      return (
        <CourseList
          key={matchingCourse.third_party_id}
          courses={[matchingCourse]}
        />
      );
    } else if (
      viewedResources?.includes(courseId as string) &&
      !viewedResources.includes(matchingCourse?.third_party_id as string)
    ) {
      return (
        <div key={courseId} className={s.courseStatus}>
          <Icon
            aria-hidden='true'
            color='currentColor'
            iconName='CheckBullet'
          />
          <div className={generateClassName(['p3', 'semiBold'])}>
            {t('academy_courseStatus_viewed')}
          </div>
        </div>
      );
    } else {
      return (
        showNextEventDetails ? 
          <NextEventDetails nextEvents={nextEvents} link={link} /> :
          <Duration duration={duration} />
        
      );
    }
  };

  return (
    <a
      className={generateClassName([s.searchResult, cardStyle.style, truncateText && s.truncate, parentBackgroundColor === 'dark' && s.onDarkBackground])}
      {...props}
      href={link}
      onClick={sendEvent}
    >
      {hasPill && (
        <div className={s.pillContainer}>
          <div
            className={generateClassName(['eyebrow'])}
            dangerouslySetInnerHTML={{ __html: cardStyle.text }}
          />
          {isLiveTraining && (
            <CountdownBadge nextEvent={nextEvents[0]} />
          )}
        </div>
      )}
      <div
        className={generateClassName([s.title, 'p2', 'semibold'])}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {!!description && (
        <div
          className={generateClassName([s.description, 'p3'])}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
      {divider && <div className={s.divider} />}
      <div className={s.postContent}>
        {renderCourseContent()}
        {/*/* TODO: partner's are not pulled in algolia current, when they are
        update isPartner with required logic (eg, tag includes 'partner)
        */}
        {/* <PartnerPill isPartner={false} /> */}
      </div>
      <CertificateBadge
        hasCertificateBadge={cardStyle.text.includes('Certificate')}
      />
    </a>
  );
};

export default UnifiedSearchResultGridItem;

